import { CameraIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { YStack } from "tamagui";
import { UploadImageInput } from "../components/chat/upload-image-input";
import { SubmitButton } from "../components/shared/buttons/submit-button";
import Card from "../components/shared/card";
import PhoneInput from "../components/shared/phone-input";
import ThemedSpinner from "../components/shared/spinner";
import TamaguiInput from "../components/shared/tamagui-input";
import Typography from "../components/shared/typography";
import {
  ContactsContextWrapper,
  useContactsContext,
} from "../hooks/ContactsContextWrapper";
import {
  FileUploadContextWrapper,
  useFileUploadContext,
} from "../hooks/FileUploadContext";
import { useToast } from "../hooks/ToastContext";

export const CreateContactPageChild = () => {
  const { create } = useContactsContext();
  const { showToast } = useToast();

  const navigate = useNavigate();

  const {
    files,
    setFiles,
    fileUploading,
    fileUploadResponse,
    uploadFilesForChat,
  } = useFileUploadContext();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");

  const handleUploadLocally = (newFiles: File[]) => {
    setFiles(newFiles);
  };

  useEffect(() => {
    if (files.length > 0 && !fileUploading) {
      uploadFilesForChat({
        files,
        additionalData: {
          purpose: "contact_image",
        },
      });
      setFiles([]);
    }
  }, [files, fileUploading, uploadFilesForChat]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      create({
        related_user: {
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
        },
      });
      navigate("/contacts");
    } catch (err) {
      console.error(err);
      setError("Failed to create contact");
    }
  };

  useEffect(() => {
    if (fileUploadResponse) {
      navigate("/contacts");
      showToast(
        "Creating contact from the photo. This may take a few seconds.",
        "success"
      );
    }
  }, [fileUploadResponse]);

  return (
    <div className="container mx-auto px-4 py-8">
      <Card
        className="w-full max-w-md mx-auto"
        variant="cream"
        elevation={2}
        hoverEffect
      >
        <Card.Header
          className="text-center flex flex-col gap-2 items-center"
          variant="cream"
        >
          <Typography variant="h3" className="text-primary">
            Create Contact
          </Typography>
          <UploadImageInput
            onUpload={handleUploadLocally}
            accept=".jpg,.jpeg,.png,.gif,.webp, .pdf"
            button={
              <button className="flex items-center gap-2">
                <CameraIcon className="w-5 h-5 text-terracotta-500 transition-colors" />
                <Typography variant="body1" className="text-terracotta">
                  Add Contact Photo
                </Typography>
              </button>
            }
          />
        </Card.Header>
        <Card.Body>
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md">
              <Typography variant="body2">{error}</Typography>
            </div>
          )}
          {fileUploading && (
            <div className="flex justify-center items-center">
              <ThemedSpinner size="lg" />
            </div>
          )}
          {!fileUploading && (
            <form onSubmit={handleSubmit}>
              <YStack gap="$3">
                <TamaguiInput
                  id="firstName"
                  label="First Name"
                  value={firstName}
                  onChangeText={setFirstName}
                  placeholder="Enter first name"
                  fullWidth
                />
                <TamaguiInput
                  id="lastName"
                  label="Last Name"
                  value={lastName}
                  onChangeText={setLastName}
                  placeholder="Enter last name"
                  fullWidth
                />
                <TamaguiInput
                  id="email"
                  label="Email"
                  inputType="email"
                  value={email}
                  onChangeText={setEmail}
                  placeholder="Enter email"
                  fullWidth
                />
                <PhoneInput
                  id="phone"
                  label="Phone"
                  value={phone}
                  onChangeText={setPhone}
                  placeholder="Enter phone"
                  fullWidth
                />

                <div className="pt-2">
                  <SubmitButton
                    onClick={() => {}}
                    onSubmit={handleSubmit}
                    isDisabled={!firstName || !lastName}
                    iconOnly={false}
                    size="md"
                    className="w-full"
                  >
                    Create Contact
                  </SubmitButton>
                </div>
              </YStack>
            </form>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export const CreateContactPage = () => (
  <FileUploadContextWrapper>
    <ContactsContextWrapper>
      <CreateContactPageChild />
    </ContactsContextWrapper>
  </FileUploadContextWrapper>
);

export default CreateContactPage;
